/**
 * @file Confirmation dialog-related text
 */
import { ALERT_SIGN_EMOJI, ARMS_CROSSED_IN_X_EMOJI, RED_HEART_EMOJI, THINKING_FACE_EMOJI } from '@@/bits/emoji'
import { __ } from '@@/bits/intl'
import {
  OzConfirmationDialogBoxButtonScheme,
  type ConfirmationDialogPayload,
} from '@@/pinia/global_confirmation_dialog'

const ALERT_ICON: Pick<ConfirmationDialogPayload, 'iconSrc'> = {
  iconSrc: ALERT_SIGN_EMOJI.iconSrc,
}

const CONFIRM_DISCARD_CHANGES: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  title: __('You have unsaved changes'),
  body: __('Are you sure you want to discard them and close this panel?'),
  confirmButtonText: __('Discard and close'),
  cancelButtonText: __('Keep working'),
}

const CONFIRM_DELETE_POST: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  ...ALERT_ICON,
  isCodeProtected: false,
  title: __('Delete post?'),
  body: __('Are you sure you want to delete this post? This cannot be undone!'),
  confirmButtonText: __('Delete'),
  cancelButtonText: __('Nevermind'),
  buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
}

const CONFIRM_DELETE_CUSTOM_TEXT_FIELD: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks' | 'title'> = {
  ...ALERT_ICON,
  isCodeProtected: false,
  confirmButtonText: __('Delete'),
  cancelButtonText: __('Nevermind'),
  buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
}

const CONFIRM_DELETE_DRAFT: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  ...ALERT_ICON,
  isCodeProtected: false,
  title: __('Delete draft?'),
  body: __('Are you sure you want to delete this draft? This cannot be undone!'),
  confirmButtonText: __('Delete'),
  cancelButtonText: __('Nevermind'),
  buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
}

const CONFIRM_DISCARD_SETTING_CHANGES: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  ...CONFIRM_DISCARD_CHANGES,
  confirmButtonText: __('Save and exit'),
  secondaryActionButtonText: __('Exit without saving'),
}

const CONFIRM_CANCEL_UPLOAD_SETTING: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  title: __('Your upload is still in progress'),
  body: __('Are you sure you want to cancel uploading?'),
  buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
  confirmButtonText: __('Cancel upload'),
  cancelButtonText: __('Nevermind'),
}

const DELETE_WALL_CONFIRMATION_PARAMS: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  isCodeProtected: true,
  title: __('Delete this padlet?'),
  body: __('This action cannot be undone.'),
  confirmButtonText: __('Delete'),
  cancelButtonText: __('Nevermind'),
  buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
  shouldFadeIn: false,
}

const PERMANENTLY_DELETE_WALL_CONFIRMATION_PARAMS: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  isCodeProtected: false,
  title: __('Permanently delete?'),
  body: __('Your padlet will be permanently deleted. You will not be able to undo this action.'),
  confirmButtonText: __('Delete'),
  cancelButtonText: __('Cancel'),
  buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
  shouldFadeIn: false,
}

const THINKING_FACE_ICON: Pick<ConfirmationDialogPayload, 'iconSrc'> = {
  iconSrc: THINKING_FACE_EMOJI.iconSrc,
}

const ARMS_CROSSED_IN_X_ICON: Pick<ConfirmationDialogPayload, 'iconSrc'> = {
  iconSrc: ARMS_CROSSED_IN_X_EMOJI.iconSrc,
}

const CONFIRM_MODERATION_REJECT_POST: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  ...ARMS_CROSSED_IN_X_ICON,
  isCodeProtected: false,
  title: __('Reject this post?'),
  body: __("Are you sure you want to reject this post? It will be moved back to the writer's drafts."),
  confirmButtonText: __('Reject'),
  cancelButtonText: __('Nevermind'),
  buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
}

const CONFIRM_MODERATION_REJECT_COMMENT: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  ...ARMS_CROSSED_IN_X_ICON,
  isCodeProtected: false,
  title: __('Reject this comment?'),
  body: __('Are you sure you want to reject this comment? It will be deleted permanently.'),
  confirmButtonText: __('Reject'),
  cancelButtonText: __('Nevermind'),
  buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
}

const RED_HEART_ICON: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks' | 'title'> = {
  iconSrc: RED_HEART_EMOJI.iconSrc,
}
const SELF_HARM_POST_ALERT: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  ...RED_HEART_ICON,
  isCodeProtected: false,
  title: __('Is there something we can do to help?'),
  body: __(
    'Based on your post it seems like you might be going through a difficult time. If you’d like to talk with someone, consider reaching out for free, confidential support.',
  ),
  confirmButtonText: __('Find a helpline'),
  cancelButtonText: __('Not now'),
  forceFullWidthButtons: true,
}

const SELF_HARM_COMMENT_ALERT: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  ...RED_HEART_ICON,
  isCodeProtected: false,
  title: __('Is there something we can do to help?'),
  body: __(
    'Based on your comment it seems like you might be going through a difficult time. If you’d like to talk with someone, consider reaching out for free, confidential support.',
  ),
  confirmButtonText: __('Find a helpline'),
  cancelButtonText: __('Not now'),
  forceFullWidthButtons: true,
}

const CONFIRM_TURN_OFF: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks' | 'title'> = {
  ...ALERT_ICON,
  confirmButtonText: __('Turn off'),
  cancelButtonText: __('Nevermind'),
  buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
  forceFullWidthButtons: true,
}

const CONFIRM_REPLACE_ATTACHMENT: Omit<ConfirmationDialogPayload, 'afterConfirmCallbacks'> = {
  ...ALERT_ICON,
  title: __('An attachment already exists'),
  body: __('Are you sure you want to replace your current attachment?'),
  confirmButtonText: __('Replace'),
  cancelButtonText: __('Cancel'),
}

export {
  ALERT_ICON,
  CONFIRM_CANCEL_UPLOAD_SETTING,
  CONFIRM_DELETE_CUSTOM_TEXT_FIELD,
  CONFIRM_DELETE_DRAFT,
  CONFIRM_DELETE_POST,
  CONFIRM_DISCARD_CHANGES,
  CONFIRM_DISCARD_SETTING_CHANGES,
  CONFIRM_MODERATION_REJECT_COMMENT,
  CONFIRM_MODERATION_REJECT_POST,
  CONFIRM_REPLACE_ATTACHMENT,
  CONFIRM_TURN_OFF,
  DELETE_WALL_CONFIRMATION_PARAMS,
  PERMANENTLY_DELETE_WALL_CONFIRMATION_PARAMS,
  SELF_HARM_COMMENT_ALERT,
  SELF_HARM_POST_ALERT,
  THINKING_FACE_ICON,
}
